import { T, useT } from '@transifex/react';
import { Pressable, VStack } from '@vidahealth/vida-ui';
import { useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { trackEvent } from '@/modules/analytics';
import { filterPartnerOrgs, type PartnerOrg } from '@/modules/api';
import {
  ELIGIBILITY_CHECK_FIELDS,
  safeDecodeOrgParam,
  useOnboardingSteps,
  useRulesEngine,
} from '@/modules/rulesEngine';
import {
  Autocomplete,
  CtaButton,
  LoadingScreen,
  Screen,
  ScreenHeading,
  SearchIcon,
  XIcon,
} from '@/modules/ui';

import { MAX_RESULTS } from './constants';

// Hoisted out of the component so Autocomplete (which feeds these into its
// virtualized result list) receives a stable identity across renders.
const getPartnerOrgKey = (org: PartnerOrg) => org.key;
const getPartnerOrgLabel = (org: PartnerOrg) => org.name;

export function CompanySearchScreen() {
  const t = useT();
  const {
    draft,
    user,
    partners,
    selectedOrg,
    loadPartners,
    setSelectedOrg,
    runEligibilityCheck,
    eligibilityCheckResult,
  } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();

  // `org` — incoming hint from a deep link or AccountCreationScreen. When
  // present the screen auto-advances without user interaction.
  const { org: orgParam } = useLocalSearchParams<{ org?: string }>();

  const [query, setQuery] = useState('');

  // Flipped on when Continue is pressed without a selection; cleared as
  // soon as the user starts interacting again.
  const [showRequiredError, setShowRequiredError] = useState(false);

  const onChangeText = (text: string) => {
    setQuery(text);
    setShowRequiredError(false);
  };

  const onSelect = (org: PartnerOrg) => {
    setQuery(org.name);
    setShowRequiredError(false);
    setSelectedOrg(org);
  };

  const onClear = () => {
    setQuery('');
    setShowRequiredError(false);
    setSelectedOrg(undefined);
  };

  // On blur, discard a partial query the user typed but never picked from the
  // list (e.g. "vida" without selecting "Vida") — a lingering unmatched value
  // reads as a choice but isn't one. A confirmed selection (the input equals
  // the picked org's name) is left intact.
  const onBlur = () => {
    if (!selectedOrg || query !== selectedOrg.name) onClear();
  };

  const onContinue = useCallback(async () => {
    if (!selectedOrg) {
      setShowRequiredError(true);
      return;
    }

    // Mirror Sparta's CompanySearchContainer: partner + paying-org are the same
    // selected org at this point. Org identifiers only — no PII.
    trackEvent('company-search-click-continue', {
      partnerUuid: selectedOrg.uuid,
      partnerName: selectedOrg.name,
      payingOrgUuid: selectedOrg.uuid,
      payingOrgName: selectedOrg.name,
    });

    const shouldTryImmediateEligibilityCheck =
      selectedOrg.supportsEligibilityCheck &&
      (selectedOrg.eligibilityCheckFields === ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB ||
        selectedOrg.eligibilityCheckFields === ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_OR_EMPID_DOB);

    if (shouldTryImmediateEligibilityCheck) {
      void runEligibilityCheck();
    } else {
      goToNextStep();
    }
  }, [selectedOrg, runEligibilityCheck, goToNextStep]);

  // Kick off the catalog fetch only from the initial 'unset' state. Gating on
  // `!== 'ready'` would re-fire on 'error' (loadPartners sets status to error
  // and clears its in-flight ref), spinning up a fresh request immediately and
  // looping. 'error' is a stable terminal state the UI surfaces instead.
  useEffect(() => {
    // Wait for the user before loading the catalog: it must be fetched with the
    // user's `isTester` (so tester accounts get tester-only orgs), and this
    // screen can mount under the loading overlay before the bootstrap user
    // fetch resolves. `user` is in the deps so the load fires once it lands.
    if (partners.status === 'unset' && user) {
      void loadPartners(user.isTester);
    }
  }, [partners.status, user, loadPartners]);

  // True when the screen was reached via an `?org=` deep-link hint that
  // matches the hydrated selectedOrg — i.e. it's a pass-through that will
  // auto-advance (fire the eligibility check or route onward), not a screen
  // the user means to interact with. Case-insensitive match mirrors the
  // provider's hydration matcher so `?org=Sana` and `?org=sana` both resolve
  // against the same partner. A manually-typed selection (no `?org=`, or a
  // different org than the hint) is not auto-advancing — the user stays on
  // the search form and drives Continue themselves.
  const isAutoAdvancing = useMemo(() => {
    if (!selectedOrg) return false;
    const decoded = safeDecodeOrgParam(orgParam);
    if (decoded) {
      return selectedOrg.key === decoded || selectedOrg.name.toLowerCase() === decoded;
    }
    return query.trim() === '' && draft.orgKey === selectedOrg.key;
  }, [draft.orgKey, orgParam, query, selectedOrg]);

  // React to the org hint resolving. Safe to depend on `onContinue` directly:
  // both `runEligibilityCheck` and `goToNextStep` are stable (RulesEngineContext
  // reads their inputs via refs), so `onContinue` only flips identity when
  // `selectedOrg` changes — which is exactly the trigger we want, and it runs
  // once per resolved hint.
  useEffect(() => {
    if (isAutoAdvancing) onContinue();
  }, [isAutoAdvancing, onContinue]);

  // Selecting fills the input with the org name; suppress the dropdown in that
  // exact case so it doesn't immediately re-open showing the single match.
  const isSelectionConfirmed = selectedOrg && query === selectedOrg.name;
  const options = useMemo(() => {
    if (isSelectionConfirmed || partners.status !== 'ready') return [];
    return filterPartnerOrgs(query, partners.data).slice(0, MAX_RESULTS);
  }, [query, isSelectionConfirmed, partners]);

  const isCatalogLoading = partners.status === 'loading';
  const errorMessage = showRequiredError ? t('Required') : undefined;

  // Pass-through: the screen auto-fires the check / routes onward, so render
  // loading rather than flashing the search form for a frame on the way to
  // employee-search / eligible / zip / invite.
  if (isAutoAdvancing) {
    return <LoadingScreen />;
  }

  return (
    <Screen
      footer={
        <CtaButton
          isBusy={eligibilityCheckResult?.isChecking === true}
          isDisabled={eligibilityCheckResult?.isChecking === true}
          onPress={onContinue}
          testID='company-search-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Please enter the name of the company providing Vida for you')}
          subtitle={t(
            'This will either be your current health insurance or current employer that you received your invitation from. Begin typing in the field below to see a list of results.'
          )}
        />

        <Autocomplete
          value={query}
          onChangeText={onChangeText}
          onBlur={onBlur}
          options={options}
          onSelect={onSelect}
          getOptionKey={getPartnerOrgKey}
          getOptionLabel={getPartnerOrgLabel}
          placeholder={t('Search')}
          label={<T _str='Employer or Health Insurance' />}
          startSlot={<SearchIcon size={18} className='text-typography-500' />}
          endSlot={
            query.length > 0 ? (
              <Pressable
                onPress={onClear}
                testID='company-search-clear'
                accessibilityRole='button'
                accessibilityLabel={t('Clear search')}
                hitSlop={8}
              >
                <XIcon size={18} className='text-typography-500' />
              </Pressable>
            ) : isCatalogLoading ? (
              <Animated.View entering={FadeIn.duration(160)} exiting={FadeOut.duration(160)}>
                <ActivityIndicator size='small' />
              </Animated.View>
            ) : undefined
          }
          loading={isCatalogLoading && query.trim().length > 0}
          loadingMessage={t('Loading companies…')}
          emptyMessage={
            partners.status === 'error'
              ? t("We couldn't load the company list right now.")
              : undefined
          }
          errorMessage={errorMessage}
          testID='company-search'
        />
      </VStack>
    </Screen>
  );
}
